import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementCitationStatus,
  StatementReferenceHelpRequest,
} from './extractedStatements';

/**
 * One expert message's reference-assistant state, as the
 * statement-references API returns it, and the predicates that decide
 * when the ExtractedStatementsBubble and the add-references prompt apply.
 *
 * The chat looks a message's state up by its SID in the
 * `['statementReferences', conversationId]` query. A message with no entry
 * has no statements, so neither bubble draws.
 */
export interface StatementReferenceState {
  messageSid: string;
  authorId: number;
  /** The pipeline writes statements as soon as it extracts them, but the
   * bubble renders only once a reviewer has approved them. */
  approved: boolean;
  /** Ordered by statement index. */
  extractedStatements: ExtractedStatement[];
  /** Multiple entries may share a `statementIndex`. */
  attachedStatementReferences: AttachedStatementReference[];
  /** Author-only intent. Empty for anyone but the author; a statement with
   * no entry resolves to `needsCitation: false` (see `resolveNeedsCitation`). */
  statementCitationStatuses: StatementCitationStatus[];
  /** Author-only, at most one per statement. Empty for anyone but the
   * author (see `resolveHelpRequestForStatement`). */
  statementReferenceHelpRequests: StatementReferenceHelpRequest[];
}

type ApprovalFacts = Pick<StatementReferenceState, 'approved' | 'extractedStatements'>;

/**
 * Predicate for the ExtractedStatementsBubble picker. The bubble renders
 * only when a reviewer has approved the statements AND there is at least
 * one.
 */
export function hasApprovedExtractedStatements(state: ApprovalFacts | undefined): boolean {
  if (!state?.approved) {
    return false;
  }

  return state.extractedStatements.length > 0;
}

/**
 * Predicate composed by the chat to decide whether the
 * `AddReferencesPromptBubble` should render below an expert message.
 *
 * Returns true only when ALL of these hold:
 * - The message qualifies for the `ExtractedStatementsBubble`
 *   (`hasApprovedExtractedStatements`).
 * - The viewer is the message author (`isCurrentUserMessage`).
 * - The message is NOT being rendered with anonymous caption chrome
 *   (`isCaptionViewer`), UNLESS it is an expert-relay message. Expert
 *   relays always render the caption, but the relay sender is the
 *   author and still owns citation attach, so the caption gate is
 *   lifted for them. The submitter-side support-label caption (the
 *   other `showMessageCaptionForViewer` branch) is not an expert relay,
 *   so it stays gated out.
 */
export function shouldShowAddReferencesPrompt(args: {
  state: ApprovalFacts | undefined;
  isCurrentUserMessage: boolean;
  isCaptionViewer: boolean;
  isExpertRelayMessage: boolean;
}): boolean {
  if (args.isCaptionViewer && !args.isExpertRelayMessage) {
    return false;
  }

  if (!args.isCurrentUserMessage) {
    return false;
  }

  return hasApprovedExtractedStatements(args.state);
}
